import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Autocomplete, Box, Callout, Column, ConfirmationModal, IconButton, Row, TextButton, TextField, Typography } from 'seagull';
import { CloseCross, PlusCircle } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { isPresetMapped } from '~/features/salesTax/presets';
import { getComponentSuggestions } from '~/features/salesTax/suggestions';
import { useLocaleKeys } from '~/i18n';
import { clampRateInput } from '../../SalesTaxSettings.utils';
import { emptyComponentRow, hasErrors, toFormValues, toPreset, validatePreset } from './CreateEditPresetModal.utils';
export const CreateEditPresetModal = (param)=>{
    let { mode, initial, isNameDuplicate, onSave, dismissModal } = param;
    const { t } = useLocaleKeys('pages.salesTaxSettings.presetModal');
    const [values, setValues] = useState(()=>toFormValues(initial, generateUniqueId('tax-comp')));
    const [submitted, setSubmitted] = useState(false);
    const suggestions = useMemo(()=>getComponentSuggestions(), []);
    const errorTexts = {
        missingRateName: t.errors.missingRateName(),
        missingComponentName: t.errors.missingComponentName(),
        missingRate: t.errors.missingRate(),
        duplicateName: t.errors.duplicateName(),
        rateNotPositive: t.errors.rateNotPositive()
    };
    const errors = validatePreset({
        values,
        duplicateName: isNameDuplicate(values.name),
        errorTexts
    });
    const showErrors = submitted;
    const setName = (name)=>setValues((v)=>_object_spread_props(_object_spread({}, v), {
                name
            }));
    const updateComponent = (index, patch)=>setValues((v)=>_object_spread_props(_object_spread({}, v), {
                components: v.components.map((c, i)=>i === index ? _object_spread({}, c, patch) : c)
            }));
    const removeComponent = (index)=>setValues((v)=>_object_spread_props(_object_spread({}, v), {
                components: v.components.filter((_c, i)=>i !== index)
            }));
    const addComponent = ()=>setValues((v)=>_object_spread_props(_object_spread({}, v), {
                components: [
                    ...v.components,
                    emptyComponentRow(generateUniqueId('tax-comp'))
                ]
            }));
    const handleSubmit = ()=>{
        setSubmitted(true);
        if (hasErrors(errors)) return;
        onSave(toPreset(values, initial, ()=>generateUniqueId('tax-preset')));
        dismissModal();
    };
    const editingMappedComponent = mode === 'edit' && isPresetMapped(initial) ? values.components.find((c, i)=>{
        var _initial_components_i;
        const originalRate = initial === null || initial === void 0 ? void 0 : (_initial_components_i = initial.components[i]) === null || _initial_components_i === void 0 ? void 0 : _initial_components_i.rate;
        return originalRate != null && originalRate !== c.rate;
    }) : undefined;
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: mode === 'create' ? t.createTitle() : t.editTitle(),
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            children: [
                /*#__PURE__*/ _jsx(TextField, {
                    bi: {
                        name: 'sales_tax_preset_name'
                    },
                    label: t.nameLabel(),
                    value: values.name,
                    onChange: (event)=>setName(event.target.value),
                    error: showErrors && errors.name != null,
                    helperText: showErrors ? errors.name : undefined
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    children: t.componentsTitle()
                }),
                values.components.map((component, index)=>{
                    var _errors_components_index, _errors_components_index1, _errors_components_index2, _errors_components_index3;
                    return /*#__PURE__*/ _jsxs(Row, {
                        gap: 12,
                        alignItems: "center",
                        children: [
                            /*#__PURE__*/ _jsx(Box, {
                                style: {
                                    flex: 1
                                },
                                children: /*#__PURE__*/ _jsx(Autocomplete, {
                                    bi: {
                                        name: 'sales_tax_preset_component_name'
                                    },
                                    style: {
                                        width: '100%'
                                    },
                                    freeSolo: true,
                                    options: suggestions,
                                    inputValue: component.name,
                                    value: component.name,
                                    onInputChange: (_event, next)=>updateComponent(index, {
                                            name: next
                                        }),
                                    onChange: (_event, next)=>{
                                        if (next == null) return;
                                        if (typeof next === 'string') {
                                            updateComponent(index, {
                                                name: next
                                            });
                                            return;
                                        }
                                        updateComponent(index, {
                                            name: next.name,
                                            rate: next.rate
                                        });
                                    },
                                    getOptionLabel: (option)=>typeof option === 'string' ? option : option.name,
                                    renderOption: (option)=>/*#__PURE__*/ _jsxs(Column, {
                                            gap: 2,
                                            children: [
                                                /*#__PURE__*/ _jsx(Typography, {
                                                    children: option.name
                                                }),
                                                option.subLabel != null && /*#__PURE__*/ _jsx(Typography, {
                                                    variant: "paragraph3",
                                                    skin: "secondary",
                                                    children: option.subLabel
                                                })
                                            ]
                                        }),
                                    textFieldProps: {
                                        label: t.componentNameLabel(),
                                        placeholder: t.componentNamePlaceholder(),
                                        error: showErrors && ((_errors_components_index = errors.components[index]) === null || _errors_components_index === void 0 ? void 0 : _errors_components_index.name) != null,
                                        helperText: showErrors ? (_errors_components_index1 = errors.components[index]) === null || _errors_components_index1 === void 0 ? void 0 : _errors_components_index1.name : undefined
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(Box, {
                                width: 140,
                                children: /*#__PURE__*/ _jsx(TextField, {
                                    bi: {
                                        name: 'sales_tax_preset_component_rate'
                                    },
                                    label: t.rateLabel(),
                                    value: component.rate,
                                    onChange: (event)=>updateComponent(index, {
                                            rate: clampRateInput(event.target.value)
                                        }),
                                    suffix: "%",
                                    error: showErrors && ((_errors_components_index2 = errors.components[index]) === null || _errors_components_index2 === void 0 ? void 0 : _errors_components_index2.rate) != null,
                                    helperText: showErrors ? (_errors_components_index3 = errors.components[index]) === null || _errors_components_index3 === void 0 ? void 0 : _errors_components_index3.rate : undefined
                                })
                            }),
                            values.components.length > 1 && /*#__PURE__*/ _jsx(IconButton, {
                                bi: {
                                    name: 'sales_tax_preset_component_remove'
                                },
                                onClick: ()=>removeComponent(index),
                                "aria-label": t.removeComponent(),
                                noPadding: true,
                                skin: "transparent",
                                size: "xsmall",
                                children: /*#__PURE__*/ _jsx(CloseCross, {})
                            })
                        ]
                    }, component.id);
                }),
                editingMappedComponent != null && /*#__PURE__*/ _jsx(Callout, {
                    intent: "warning",
                    children: t.mappingBreakCallout()
                }),
                /*#__PURE__*/ _jsx(Row, {
                    children: /*#__PURE__*/ _jsx(TextButton, {
                        bi: {
                            name: 'sales_tax_preset_add_component'
                        },
                        underline: "none",
                        onClick: addComponent,
                        children: /*#__PURE__*/ _jsxs(Row, {
                            gap: 6,
                            alignItems: "center",
                            children: [
                                /*#__PURE__*/ _jsx(PlusCircle, {
                                    size: 18
                                }),
                                t.addComponent()
                            ]
                        })
                    })
                })
            ]
        }),
        onPrimaryButtonClick: handleSubmit,
        onSecondaryButtonClick: dismissModal,
        primaryButtonLabel: mode === 'create' ? t.create() : t.save(),
        secondaryButtonLabel: t.cancel(),
        textAlignCenter: false,
        bi: {
            primaryButton: {
                name: 'sales_tax_preset_save'
            },
            secondaryButton: {
                name: 'sales_tax_preset_cancel'
            }
        }
    });
};
